import { Title } from "@solidjs/meta";
import { createSignal, For, Show, untrack, type JSX } from "solid-js";
import { createStoredSignal } from "~/util/stored";
import {
  BaseErrors,
  Form,
  InputField,
  RadioGroupField,
  RadioGroupFieldOption,
  TextAreaField,
  UploaderField,
} from "~/common/form";
import { Button } from "~/common/button";
import { PublicFormShell } from "~/common/public_form/shell";
import { PublicFormHeader } from "~/common/public_form/header";
import { SuccessCheck } from "~/common/public_form/success_check";
import { buildFormData, currentFormPath, submitPublicForm } from "~/embeds/public_form";
import { INTENT_OPTIONS, URGENCY_OPTIONS } from "~/referrals/options";

type Location = {
  id: string;
  name: string;
  address: string | null;
  phone: string | null;
  logoUrl: string | null;
};

type Props = {
  location: Location;
};

// The referring office's own details — persisted locally so a repeat referrer doesn't retype them.
type ReferrerFields = {
  referrerPracticeName: string;
  referrerProviderFirstName: string;
  referrerProviderLastName: string;
  referrerPhone: string;
  referrerEmail: string;
};

// Per-submission fields — never persisted (they change every referral, and patient data is PHI).
type DetailFields = {
  submittedPatientFirstName: string;
  submittedPatientLastName: string;
  submittedPatientDateOfBirth: string;
  submittedPatientPhone: string;
  submittedPatientEmail: string;
  reason: string;
  toothArea: string;
  referralIntent: string;
  notes: string;
  urgency: string;
};

const ATTACHMENT_ACCEPT = ".pdf,.jpg,.jpeg,.png,image/*,application/pdf";

const EMPTY_REFERRER: ReferrerFields = {
  referrerPracticeName: "",
  referrerProviderFirstName: "",
  referrerProviderLastName: "",
  referrerPhone: "",
  referrerEmail: "",
};

function buildInitialDetails(): DetailFields {
  return {
    submittedPatientFirstName: "",
    submittedPatientLastName: "",
    submittedPatientDateOfBirth: "",
    submittedPatientPhone: "",
    submittedPatientEmail: "",
    reason: "",
    toothArea: "",
    referralIntent: "evaluate_and_treat",
    notes: "",
    urgency: "routine",
  };
}

export default function ReferralIntakeForm(props: Props) {
  // createStoredSignal persists to localStorage (read on init, written on change); only the
  // referring office's details are stored, never the patient/referral details.
  const [referrer, setReferrer] = createStoredSignal<ReferrerFields>({
    key: untrack(() => `referral-intake-referrer:${props.location.id}`),
    fallbackValue: EMPTY_REFERRER,
  });
  const [details, setDetails] = createSignal<DetailFields>(untrack(() => buildInitialDetails()));
  const [attachments, setAttachments] = createSignal<File[]>([]);
  const [submitting, setSubmitting] = createSignal(false);
  // Field errors are keyed by snake_case param name (server's inertia_errors); form errors are
  // non-field failures (network / unexpected server error) shown in a summary.
  const [fieldErrors, setFieldErrors] = createSignal<Record<string, string[]>>({});
  const [formErrors, setFormErrors] = createSignal<string[]>([]);
  const [submitted, setSubmitted] = createSignal(false);

  const updateReferrer = (partial: Partial<ReferrerFields>) =>
    setReferrer((prev) => ({ ...prev, ...partial }));
  const updateDetails = (partial: Partial<DetailFields>) =>
    setDetails((prev) => ({ ...prev, ...partial }));

  const fieldError = (key: string) => fieldErrors()[key];

  const addAttachments = (files: File[]) => setAttachments((prev) => [...prev, ...files]);
  const removeAttachment = (index: number) =>
    setAttachments((prev) => prev.filter((_, i) => i !== index));

  const reset = () => {
    setDetails(buildInitialDetails());
    setAttachments([]);
    setFieldErrors({});
    setFormErrors([]);
    setSubmitted(false);
  };

  const buildPayload = (): FormData => {
    // Normalize the persisted referrer to the current shape — a blob saved before a field was added
    // would otherwise omit that key, and FormData stringifies the resulting `undefined` to "undefined".
    const r = { ...EMPTY_REFERRER, ...referrer() };
    const d = details();
    return buildFormData(
      "referral_intake",
      {
        submitted_patient_first_name: d.submittedPatientFirstName,
        submitted_patient_last_name: d.submittedPatientLastName,
        submitted_patient_date_of_birth: d.submittedPatientDateOfBirth,
        submitted_patient_phone: d.submittedPatientPhone,
        submitted_patient_email: d.submittedPatientEmail,
        referrer_practice_name: r.referrerPracticeName,
        referrer_provider_first_name: r.referrerProviderFirstName,
        referrer_provider_last_name: r.referrerProviderLastName,
        referrer_phone: r.referrerPhone,
        referrer_email: r.referrerEmail,
        reason: d.reason,
        tooth_area: d.toothArea,
        referral_intent: d.referralIntent,
        notes: d.notes,
        urgency: d.urgency,
      },
      { attachments: attachments() },
    );
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setSubmitting(true);
    setFieldErrors({});
    setFormErrors([]);

    const result = await submitPublicForm(currentFormPath(), buildPayload());
    if (result.success) {
      setSubmitted(true);
    } else {
      setFieldErrors(result.fieldErrors);
      setFormErrors(result.formErrors);
    }
    setSubmitting(false);
  };

  return (
    <>
      <Title>{props.location.name} — Refer a patient</Title>

      <PublicFormShell width="lg">
        <PublicFormHeader
          location={props.location}
          class="border-b border-brand-subtle bg-brand-light px-6 py-5 sm:px-10"
        />

        <Show
          when={!submitted()}
          fallback={<Confirmation location={props.location} onReset={reset} />}
        >
          <Form
            class="gap-8 px-6 py-8 sm:px-10"
            fieldWidth="full"
            onSubmit={handleSubmit}
            novalidate
          >
            <div>
              <h1 class="text-2xl font-semibold text-typography-primary">Refer a patient</h1>
              <p class="mt-2 text-sm text-typography-secondary">
                Send a referral to {props.location.name}. The office will follow up with you and the
                patient.
              </p>
            </div>

            <Section title="Patient">
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InputField
                  label="First name"
                  required
                  value={details().submittedPatientFirstName}
                  errors={fieldError("submitted_patient_first_name")}
                  onInput={(e) =>
                    updateDetails({ submittedPatientFirstName: e.currentTarget.value })
                  }
                />
                <InputField
                  label="Last name"
                  required
                  value={details().submittedPatientLastName}
                  errors={fieldError("submitted_patient_last_name")}
                  onInput={(e) =>
                    updateDetails({ submittedPatientLastName: e.currentTarget.value })
                  }
                />
              </div>
              <InputField
                label="Date of birth"
                type="date"
                required
                value={details().submittedPatientDateOfBirth}
                errors={fieldError("submitted_patient_date_of_birth")}
                onInput={(e) =>
                  updateDetails({ submittedPatientDateOfBirth: e.currentTarget.value })
                }
              />
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InputField
                  label="Phone"
                  type="tel"
                  inputMode="tel"
                  required
                  value={details().submittedPatientPhone}
                  errors={fieldError("submitted_patient_phone")}
                  onInput={(e) => updateDetails({ submittedPatientPhone: e.currentTarget.value })}
                />
                <InputField
                  label="Email"
                  type="email"
                  inputMode="email"
                  value={details().submittedPatientEmail}
                  errors={fieldError("submitted_patient_email")}
                  onInput={(e) => updateDetails({ submittedPatientEmail: e.currentTarget.value })}
                />
              </div>
            </Section>

            <Section title="Referring office">
              <InputField
                label="Practice name"
                required
                value={referrer().referrerPracticeName}
                errors={fieldError("referrer_practice_name")}
                onInput={(e) => updateReferrer({ referrerPracticeName: e.currentTarget.value })}
              />
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InputField
                  label="Practice phone"
                  type="tel"
                  inputMode="tel"
                  required
                  value={referrer().referrerPhone}
                  errors={fieldError("referrer_phone")}
                  onInput={(e) => updateReferrer({ referrerPhone: e.currentTarget.value })}
                />
                <InputField
                  label="Practice email"
                  type="email"
                  inputMode="email"
                  value={referrer().referrerEmail}
                  errors={fieldError("referrer_email")}
                  onInput={(e) => updateReferrer({ referrerEmail: e.currentTarget.value })}
                />
              </div>
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InputField
                  label="Provider first name"
                  required
                  value={referrer().referrerProviderFirstName}
                  errors={fieldError("referrer_provider_first_name")}
                  onInput={(e) =>
                    updateReferrer({ referrerProviderFirstName: e.currentTarget.value })
                  }
                />
                <InputField
                  label="Provider last name"
                  required
                  value={referrer().referrerProviderLastName}
                  errors={fieldError("referrer_provider_last_name")}
                  onInput={(e) =>
                    updateReferrer({ referrerProviderLastName: e.currentTarget.value })
                  }
                />
              </div>
            </Section>

            <Section title="Referral details">
              <TextAreaField
                label="Reason for referral"
                required
                rows={4}
                value={details().reason}
                errors={fieldError("reason")}
                onInput={(e) => updateDetails({ reason: e.currentTarget.value })}
                placeholder="Describe the reason for this referral"
              />
              <InputField
                label="Tooth / area"
                value={details().toothArea}
                errors={fieldError("tooth_area")}
                onInput={(e) => updateDetails({ toothArea: e.currentTarget.value })}
              />
              <RadioGroupField
                label="What are you requesting?"
                orientation="horizontal"
                errors={fieldError("referral_intent")}
              >
                <For each={INTENT_OPTIONS}>
                  {(option) => (
                    <RadioGroupFieldOption
                      name="referralIntent"
                      value={option.value}
                      label={option.label}
                      checked={details().referralIntent === option.value}
                      onChange={() => updateDetails({ referralIntent: option.value })}
                    />
                  )}
                </For>
              </RadioGroupField>
              <RadioGroupField
                label="Urgency"
                orientation="horizontal"
                errors={fieldError("urgency")}
              >
                <For each={URGENCY_OPTIONS}>
                  {(option) => (
                    <RadioGroupFieldOption
                      name="urgency"
                      value={option.value}
                      label={option.label}
                      checked={details().urgency === option.value}
                      onChange={() => updateDetails({ urgency: option.value })}
                    />
                  )}
                </For>
              </RadioGroupField>
              <TextAreaField
                label="Notes"
                rows={3}
                value={details().notes}
                errors={fieldError("notes")}
                onInput={(e) => updateDetails({ notes: e.currentTarget.value })}
                placeholder="Anything else — radiograph handling, premedication, callback preference"
              />
              <UploaderField
                label="Attachments"
                description="Optionally attach X-rays, charts, or other records (PDF, JPG, or PNG)."
                accept={ATTACHMENT_ACCEPT}
                multiple
                files={attachments()}
                onFiles={addAttachments}
                onRemove={removeAttachment}
                errors={fieldError("attachments")}
              />
            </Section>

            <div class="flex flex-col gap-3">
              <BaseErrors errors={formErrors()} />
              <div class="flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  loading={submitting()}
                  disabled={submitting()}
                >
                  Submit referral
                </Button>
              </div>
            </div>
          </Form>
        </Show>
      </PublicFormShell>
    </>
  );
}

function Section(props: { title: string; description?: string; children: JSX.Element }) {
  return (
    <div class="flex flex-col gap-4">
      <div>
        <h2 class="text-base font-semibold text-typography-primary">{props.title}</h2>
        <Show when={props.description}>
          <p class="mt-1 text-sm text-typography-secondary">{props.description}</p>
        </Show>
      </div>
      {props.children}
    </div>
  );
}

function Confirmation(props: { location: Location; onReset: () => void }) {
  return (
    <div class="flex flex-col items-center gap-6 px-6 py-12 text-center sm:px-10">
      <SuccessCheck />

      <div>
        <h2 class="text-2xl font-semibold text-typography-primary">Referral submitted</h2>
        <p class="mt-2 text-sm text-balance text-typography-secondary">
          Thanks for your referral. {props.location.name} will review the details and follow up with
          you and the patient.
        </p>
      </div>

      <Button variant="secondary" onClick={() => props.onReset()}>
        Submit another referral
      </Button>
    </div>
  );
}
